import * as React from 'react';

import {
  BookingSortOption,
  enumBookingSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../_state/bookingsList.state';
import { createBookingsVoidValidationFetcher } from '../../_state/bookingsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { BookingRecord } from '../../types/booking';
import { enumEntityType, VoidEntityDialog } from '../_shared/VoidEntityDialog';
import { LoadCreate } from '../loads/LoadCreate';

import { BookingDialog } from './BookingDialog';
import { BookingListFilters } from './BookingListFilters';
import { BookingsListFloatingToolbar } from './BookingsListFloatingToolbar';
import { useBookingsListColumns } from './hooks/useBookingsListColumns';

import useDebouncedValue from '~/shared/hooks/useDebounce';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: BookingSortOption;
    desc: BookingSortOption;
  }
> = {
  bookingNumber: {
    asc: enumBookingSortOption.DocumentNumberAsc,
    desc: enumBookingSortOption.DocumentNumberDesc,
  },
  status: {
    asc: enumBookingSortOption.StatusAsc,
    desc: enumBookingSortOption.StatusDesc,
  },
  cutOffDate: {
    asc: enumBookingSortOption.CutOffDateAsc,
    desc: enumBookingSortOption.CutOffDateDesc,
  },
  createdAt: {
    asc: enumBookingSortOption.CreatedAtAsc,
    desc: enumBookingSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ BOOKINGS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BookingsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const {
    data,
    isFetching,
    loadingState,
    error,
    refetch: refetchBookings,
  } = useAtomValue(state.$remoteBookings);
  const { refetch: refetchBookingsCountingByStatus } = useAtomValue(
    state.$bookingsCountingByStatus,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isDialogOpen, setIsDialogOpen] = useAtom(state.$isDialogOpen);
  const [editBooking, setEditBooking] = useAtom(state.$editBooking);
  const [selectedStatus] = useAtom(state.$selectedStatus);
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useAtom(
    state.$isLoadDialogOpen,
  );
  const [selectedBooking, setSelectedBooking] = useAtom(state.$selectedBooking);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const [bulkVoidBookings, setBulkVoidBookings] = useAtom(
    state.$bulkVoidBookings,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const bulkVoidBookingMutation = useSetAtom(state.$bulkVoidBooking);
  const voidBooking = useSetAtom(state.$voidBooking);

  const bookings = data?.bookings ?? [];
  const total = data?.total ?? 0;

  // Other data hooks
  const toast = useToast();
  const selectedRows = useMemoDataGridRows(bookings, selectedKeys);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);

  // Memoized values
  const fetchBookingsVoidValidation = React.useMemo(
    () => createBookingsVoidValidationFetcher(bulkVoidBookingMutation),
    [bulkVoidBookingMutation],
  );

  // Callbacks
  const handleRefetchBookings = React.useCallback(() => {
    refetchBookings();
    refetchBookingsCountingByStatus();
  }, [refetchBookings, refetchBookingsCountingByStatus]);
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleCreate = React.useCallback(() => {
    setEditBooking(null);
    setIsDialogOpen(true);
  }, [setEditBooking, setIsDialogOpen]);
  const handleEdit = React.useCallback(
    (booking: BookingRecord) => {
      navigate(tradeRoutePaths.bookingDetail, {
        params: { id: booking.id },
      });
    },
    [navigate],
  );
  const handleBulkVoid = React.useCallback(() => {
    const bookingsToVoid = selectedRows
      .filter((row) => row.id && row.bookingNumber !== undefined)
      .map((row) => ({
        id: row.id as string,
        displayNumber: String(row.bookingNumber),
        status: row.status,
      }));

    if (bookingsToVoid.length > 0) {
      setBulkVoidBookings(bookingsToVoid);
      setIsVoidDialogOpen(true);
    }
  }, [selectedRows, setBulkVoidBookings, setIsVoidDialogOpen]);
  const handleBulkVoidComplete = React.useCallback(() => {
    const bookingNumbers = bulkVoidBookings
      .map((b) => `#${b.displayNumber}`)
      .join(', ');
    toast.success(
      t.bookingsView.messages.bulkVoidSuccess(
        bulkVoidBookings.length,
        bookingNumbers,
      ),
    );
    setSelectedKeys(new Set());
    setIsVoidDialogOpen(false);
    setBulkVoidBookings([]);
    handleRefetchBookings();
  }, [
    toast,
    t,
    bulkVoidBookings,
    setSelectedKeys,
    setIsVoidDialogOpen,
    setBulkVoidBookings,
    handleRefetchBookings,
  ]);
  const handleBulkVoidCancel = React.useCallback(() => {
    setIsVoidDialogOpen(false);
    setBulkVoidBookings([]);
  }, [setIsVoidDialogOpen, setBulkVoidBookings]);
  const handleVoid = React.useCallback(
    (booking: BookingRecord) => {
      setBulkVoidBookings([
        {
          id: booking.id as string,
          displayNumber: String(booking.bookingNumber),
          status: booking.status,
        },
      ]);
      setIsVoidDialogOpen(true);
    },
    [setBulkVoidBookings, setIsVoidDialogOpen],
  );
  const handleDoubleClick = React.useCallback(
    ({ row }: { row: BookingRecord }) => handleEdit(row),
    [handleEdit],
  );
  const handleSetIsLoadDialogOpen = React.useCallback(
    (open: boolean, bookingId?: string) => {
      if (open) {
        setSelectedBooking(
          bookingId ? ({ id: bookingId } as BookingRecord) : null,
        );
      }
      setIsLoadDialogOpen(open);
    },
    [setSelectedBooking, setIsLoadDialogOpen],
  );
  const handleVoidConfirm = React.useCallback(
    async (id: string, forceUnlink: boolean) => {
      try {
        await voidBooking({ id, forceUnlink });
      } catch (error) {
        toast.error(
          t.bookingsView.messages.actionFailed({
            action: 'void',
            message:
              error instanceof Error
                ? error.message
                : t.bookingsView.messages.unknownError,
          }),
        );
        throw error;
      }
    },
    [voidBooking, toast, t],
  );
  const handleCloseLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
    setSelectedBooking(null);
  }, [setIsLoadDialogOpen, setSelectedBooking]);
  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumBookingSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const { columns, columnVisibilityState } = useBookingsListColumns({
    selectedStatus,
    onDoubleClick: handleDoubleClick,
    setIsLoadDialogOpen: handleSetIsLoadDialogOpen,
    setSelectedBooking,
    onVoid: handleVoid,
    onRefetch: handleRefetchBookings,
  });

  const emptyPanelProps = {
    title: t.bookingsView.emptyState.title,
    description: (
      <span>
        {t.bookingsView.emptyState.subtitle(
          <strong>{t.bookingsView.emptyState.action}</strong>,
        )}
      </span>
    ),
    action: (
      <Button
        onPress={handleCreate}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.bookingsView.toolbar.create}
      </Button>
    ),
    centered: true,
  };

  // Effects
  React.useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm, setSearchTerm]);

  return (
    <ListScreenLayout
      className="@BookingsListView"
      filters={<BookingListFilters />}
      actions={
        <Button
          onPress={handleCreate}
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
        >
          {t.bookingsView.toolbar.create}
        </Button>
      }
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
    >
      <DataGrid<BookingRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={bookings}
        rowKey="id"
        aria-label={t.bookingsView.table.aria}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(keys) => {
          setSelectedKeys(keys);
        }}
        onColumnSort={handleColumnSort}
        columnVisibilityState={columnVisibilityState}
        className="w-full text-sm"
        fullWidth
        persistColumnOrderKey="trade:bookings-list"
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.bookingsView.content.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />

      <BookingsListFloatingToolbar
        selectedCount={selectedRows.length}
        onBulkVoid={handleBulkVoid}
        onClearSelection={handleClearSelection}
      />

      {isDialogOpen && (
        <BookingDialog
          booking={editBooking}
          isOpen={isDialogOpen}
          onClose={() => {
            setEditBooking(null);
            setIsDialogOpen(false);
          }}
          onSuccess={(bookingId) => {
            setEditBooking(null);
            setIsDialogOpen(false);
            if (bookingId) {
              navigate(tradeRoutePaths.bookingDetail, {
                params: { id: bookingId },
              });
            } else {
              handleRefetchBookings();
            }
          }}
        />
      )}
      <LoadCreate
        isOpen={isLoadDialogOpen}
        requiredBookingId={selectedBooking?.id}
        onClose={handleCloseLoadDialog}
        onSuccess={handleCloseLoadDialog}
      />

      {isVoidDialogOpen && bulkVoidBookings.length > 0 && (
        <VoidEntityDialog
          entityType={enumEntityType.booking}
          entities={bulkVoidBookings}
          onVoid={handleVoidConfirm}
          fetchValidation={fetchBookingsVoidValidation}
          onComplete={handleBulkVoidComplete}
          onCancel={handleBulkVoidCancel}
        />
      )}
    </ListScreenLayout>
  );
};
